import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';

const AdminTicketInbox = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tickets/${ticketId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    fetchMessages(ticket.id);
    setDialogOpen(true);
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/tickets/${ticketId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/tickets/${selectedTicket.id}/reply`,
        { message: replyText, is_internal_note: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setReplyText('');
      toast.success('Reply sent');
      fetchMessages(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      in_progress: 'bg-purple-100 text-purple-700',
      resolved: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700'
    };
    return styles[status] || styles.open;
  };

  const filterTicketsByStatus = (statusFilter) => {
    if (statusFilter === 'open') {
      return tickets.filter(t => ['open', 'pending', 'in_progress'].includes(t.status));
    }
    if (statusFilter === 'resolved') {
      return tickets.filter(t => ['resolved', 'closed'].includes(t.status));
    }
    return tickets;
  };

  if (loading) {
    return <div className="text-center py-12">Loading tickets...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800" data-testid="admin-tickets-title">Ticket Inbox</h2>
          <p className="text-gray-600 mt-1">Manage vendor support requests</p>
        </div>
        <Button
          onClick={() => navigate('/admin/dashboard')}
          variant="outline"
          className="border-green-500 text-green-700"
        >
          Back to Dashboard
        </Button>
      </div>

      <Tabs defaultValue="open" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="open" data-testid="tab-open-tickets">
            Active Tickets ({filterTicketsByStatus('open').length})
          </TabsTrigger>
          <TabsTrigger value="resolved" data-testid="tab-resolved-tickets">
            Closed / Archived ({filterTicketsByStatus('resolved').length})
          </TabsTrigger>
        </TabsList>

        {['open', 'resolved'].map(tab => (
          <TabsContent key={tab} value={tab}>
            {filterTicketsByStatus(tab).length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">No {tab === 'open' ? 'active' : 'closed'} tickets</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filterTicketsByStatus(tab).map((ticket) => (
                  <Card key={ticket.id} className="hover:shadow-lg transition-all" data-testid={`admin-ticket-${ticket.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg" data-testid={`admin-ticket-subject-${ticket.id}`}>
                            {ticket.subject}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={getStatusBadge(ticket.status)}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge className="bg-blue-100 text-blue-700">
                              {ticket.ticket_type.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleViewTicket(ticket)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`view-ticket-${ticket.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">{ticket.body}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                        <Select
                          value={ticket.status}
                          onValueChange={(value) => handleStatusChange(ticket.id, value)}
                        >
                          <SelectTrigger className="w-[140px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedTicket.subject}</DialogTitle>
                <DialogDescription>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={getStatusBadge(selectedTicket.status)}>
                      {selectedTicket.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-700">
                      {selectedTicket.ticket_type.replace('_', ' ')}
                    </Badge>
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Original Message</h3>
                  <p className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                    {selectedTicket.body}
                  </p>
                </div>

                {messages.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-3">Conversation</h3>
                    <div className="space-y-3">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`p-3 rounded-lg ${
                            message.author_role === 'admin'
                              ? 'bg-green-50 border-l-4 border-green-500'
                              : 'bg-blue-50 border-l-4 border-blue-500'
                          }`}
                        >
                          <div className="text-xs text-gray-500 mb-1">
                            {message.author_role === 'admin' ? 'Admin' : 'Vendor'} - 
                            {new Date(message.created_at).toLocaleString()}
                          </div>
                          <p className="text-sm">{message.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <form onSubmit={handleSendReply} className="space-y-3">
                  <h3 className="font-semibold">Admin Reply</h3>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    rows={4}
                    data-testid="admin-reply-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      disabled={!replyText.trim()}
                      data-testid="admin-send-reply-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                    <Select
                      value={selectedTicket.status}
                      onValueChange={(value) => handleStatusChange(selectedTicket.id, value)}
                    >
                      <SelectTrigger className="w-[160px]" data-testid="admin-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </form>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTicketInbox;